import { ModelType } from '@/modules/core/app/constants/ModelType';
import { AiBatchReviewRecordChange } from '@/modules/ai/models/AiBatchReviewRecordChange';
import Lang from '@/modules/core/app/services/Lang';
import { BaseExtractedRecordModel } from '@/modules/ai/models/abstracts/BaseExtractedRecordModel';
import { ExtractedUserCollection } from '@/modules/ai/models/ExtractedUserCollection';
import { Document } from '@/modules/document/models/Document';
import { ContactAlias } from '@/modules/contact/models/ContactAlias';
import { Address } from '@/modules/address/models/Address';
import { Contact } from '@/modules/contact/models/Contact';
import { Gender } from '@/modules/contact/enums/Gender';
import { PersonTitle } from '@/modules/contact/enums/PersonTitle';

export class ExtractedContact extends BaseExtractedRecordModel {
  id?: number;
  documentId?: number;
  contactId?: number | null;
  contact?: Contact | null;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  birthSurname?: string;
  personTitle?: string;
  isEntity?: boolean;
  isCreatedManually?: boolean;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
  gender?: string;
  document?: Relationship<Document | null>;
  extractedUsers!: Relationship<ExtractedUserCollection>;
  contactAliases?: Relationship<ContactAlias[]> | null;
  address?: Relationship<Address | null>;

  constructor(model: Partial<ExtractedContact> = {}) {
    super(model);

    this.id = model.id;
    this.documentId = model.documentId;
    this.contactId = model.contactId;
    this.firstName = model.firstName;
    this.middleName = model.middleName;
    this.lastName = model.lastName;
    this.birthSurname = model.birthSurname;
    this.personTitle = model.personTitle;
    this.isEntity = model.isEntity;
    this.isCreatedManually = model.isCreatedManually;
    this.email = model.email;
    this.phoneNumber = model.phoneNumber ?? '';
    this.dateOfBirth = model.dateOfBirth;
    this.gender = model.gender;
    this.document = Document.mapModelOrNull(model.document);
    this.extractedUsers = ExtractedUserCollection.mapModel(model.extractedUsers);
    this.contactAliases = ContactAlias.mapModelsOrNull(model.contactAliases);
    this.address = Address.mapModel(model.address);
    this.contact = Contact.mapModelOrNull(model.contact);
    this.rememberMatchOnFile();
  }

  getModelName(): ModelType {
    return ModelType.EXTRACTED_CONTACT;
  }

  get showAmountColumn(): boolean {
    return false;
  }

  get matchedRecord(): Contact | null {
    return this.contact ?? null;
  }

  protected get matchedRecordKey(): string {
    return 'contact';
  }

  protected get matchedId(): number | null | undefined {
    return this.contactId;
  }

  protected set matchedId(id: number | null | undefined) {
    this.contactId = id;
  }

  get matchedLabel(): string {
    return this.contact?.fullName ?? '';
  }

  protected get comparedValues(): (AiBatchReviewRecordChange | null)[] {
    const contact: Contact | null = this.matchedRecord;
    const date: (value: unknown) => string = (value: unknown): string => this.formatDate(value);
    const digits: (value: unknown) => string = (value: unknown): string =>
      String(value).replace(/\D/g, '');

    return [
      this.changeFor(Lang.t('contact.first-name'), contact?.firstName, this.firstName),
      this.changeFor(Lang.t('contact.middle-name'), contact?.middleName, this.middleName),
      this.changeFor(Lang.t('contact.last-name'), contact?.lastName, this.lastName),
      this.changeFor(Lang.t('contact.last-name-birth'), contact?.birthSurname, this.birthSurname),
      this.changeFor(
        Lang.t('contact.title'),
        contact?.personTitle,
        this.personTitle,
        (value: unknown): string => this.labelOf(PersonTitle, value)
      ),
      this.changeFor(
        Lang.t('contact.gender-at-birth'),
        contact?.gender,
        this.gender,
        (value: unknown): string => this.labelOf(Gender, value)
      ),
      contact?.email ? null : this.changeFor(Lang.t('contact.email'), null, this.email),
      this.changeFor(
        Lang.t('contact.phone-number'),
        contact?.phoneNumber,
        this.phoneNumber,
        String,
        digits
      ),
      this.changeFor(Lang.t('contact.date-of-birth'), contact?.dateOfBirth, this.dateOfBirth, date)
    ];
  }

  private labelOf(enumClass: typeof PersonTitle | typeof Gender, key: unknown): string {
    return enumClass.getByKey(key as string)?.value ?? String(key);
  }

  get name(): string {
    return [this.firstName, this.middleName, this.lastName]
      .filter((name: string | undefined) => !!name)
      .join(' ');
  }

  get firstLetters(): string {
    const first: string = this.firstName?.charAt(0) ?? '';
    const last: string = this.lastName?.charAt(0) ?? this.middleName?.charAt(0) ?? '';

    return `${first}${last}`.toUpperCase();
  }

  get hasName(): boolean {
    return !!this.firstName || !!this.middleName || !!this.lastName;
  }

  get hasContactAliases(): boolean {
    return !!this.contactAliases?.length;
  }

  get nameFormatted(): string {
    return this.hasName ? this.name : '-';
  }
}
